/**
 * @fileoverview WelcomePreview Component
 * 
 * This component renders a preview of the welcome page with customizable content 
 * and branding elements. It uses URL parameters to configure the welcome message,
 * banner image, and logo, allowing for client-specific UI previews.
 * 
 * Key features:
 * - Dynamically loads welcome text, banner image, and logo from URL parameters
 * - Falls back to default content from clientInfo configuration
 * - Renders multi-line welcome text with HTML support
 * - Displays customizable title and branding
 */
import React, { useEffect, useState } from "react";
import parse from 'html-react-parser';
import Header from "../../commons/Header";
import { clientInfo } from '../../commons/ClientDynamicInfo';
/**
 * WelcomePreview Component
 * 
 * Renders a preview of the welcome page with customizable content and branding.
 * Used for previewing client-specific UI configurations.
 * 
 * @returns {JSX.Element} WelcomePreview component
 */
const WelcomePreview = () => {
  // State for storing welcome text content (as array of paragraphs)
  const [welcomeText, setWelcomeText] = useState<any>("");

  // State for storing the banner/left panel image URL
  const [bannerImg, setBannerImg] = useState<any>("");

  // State for storing the header logo image URL
  const [headerImg, setHeaderImg] = useState<any>("");

  // State for storing the welcome title
  const [welcomeTitle, setWelcomeTitle] = useState<string>('Welcome to the Leader Portal');

  // Empty title for header (uses logo instead)
  const title = '';  /**
   * On component mount, set welcome text, banner image, and header logo
   * based on URL parameters or fall back to default content
   */
  useEffect(() => {
    // Parse URL parameters inside useEffect to avoid dependency issues
    const urlParams = new URLSearchParams(new URL(window.location.href).search);

    // Set welcome text from URL parameter or use default from clientInfo
    if (urlParams?.get('welcomeDesc') && urlParams?.get('welcomeDesc') !== 'undefined' && urlParams?.get('welcomeDesc') !== '') {
      setWelcomeText(urlParams?.get('welcomeDesc')?.split('\n'))
    } else {
      setWelcomeText(clientInfo['*'].welcomeText.split('\n'));
    }

    // Set banner/left panel image from URL parameter or use default
    if (urlParams?.get('leftPanelImg') && urlParams?.get('leftPanelImg') !== 'undefined' && urlParams?.get('leftPanelImg') !== '') {
      setBannerImg(urlParams?.get('leftPanelImg'));
    } else {
      setBannerImg('./../assets/img/onboarding-image.png');
    }

    // Set header logo from URL parameter or use default
    if (urlParams?.get('logoIcon') && urlParams?.get('logoIcon') !== 'undefined' && urlParams?.get('logoIcon') !== '') {
      setHeaderImg(urlParams?.get('logoIcon'));
    } else {
      setHeaderImg('./../assets/img/HlabsHeaderLogo.png');
    }

    // Set welcome title from URL parameter or keep default
    const title = urlParams?.get('welcomeTitle');
    if (title && title !== 'undefined') {
      setWelcomeTitle(title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="mainWrapper">
      {/* Left panel with background image */}
      <div className="imgWrapper" style={{ background: `url(${bannerImg}) no-repeat` }}>
        &nbsp;
      </div>
      {/* Right panel with content */}
      <div className="rightWrapper" style={{ justifyContent: "flex-start" }}>
        {/* Header component with logo */}
        <Header title={title} headerLogo={headerImg} />
        <div className="contentWrapper">
          {/* Dynamic welcome title */}
          <h1 className="titleStyle">
            {welcomeTitle}
          </h1>
          {/* Welcome message paragraphs */}
          <div className="middleContent">
            {welcomeText && welcomeText.map((text: any) => (
              <p className="welcomeContent" key={text}>{parse(text)}</p>
            ))}
          </div>
        </div>
      </div>
    </div >
  );
};

export default WelcomePreview;